/**
 * EVO — hisobotlar ma'lumot qatlami.
 *
 * ============================================================================
 * TIPLAR SERVER PROYEKSIYASIDAN NUSXA KO'CHIRILGAN
 * ============================================================================
 * Har interfeys `apps/api/src/modules/reports/reports.service.ts` dagi
 * `select({...})` bilan MAYDONMA-MAYDON mos. Bu qo'lda saqlanadigan shartnoma:
 * server ustunni qayta nomlasa, TypeScript buni ushlamaydi — javob `unknown`
 * emas, `any` bo'lib keladi. Shuning uchun har tip ustida qaysi so'rov uni
 * qaytarishi yozilgan; o'zgarish bo'lganda solishtirish uchun bitta joy.
 *
 * ============================================================================
 * IXTIYORIY MAYDON = RUXSAT YO'Q
 * ============================================================================
 * `avgCost?`, `totalValue?` — `products.ts` dagi bilan bir xil qoida: server
 * `stock.value.view` bo'lmasa maydonni javobga UMUMAN qo'shmaydi
 * (`...(showValue ? {...} : {})`), `null` ham qilmaydi. Tipda `?` turgani
 * uchun TypeScript uni tekshirmasdan ishlatishga yo'l qo'ymaydi.
 *
 * ============================================================================
 * BARCHA PUL VA MIQDOR — `string`
 * ============================================================================
 * Postgres `numeric` drayverdan matn bo'lib keladi. `number` ga aylantirish
 * TAQIQLANGAN (format.ts). `count(*)::int` esa haqiqiy `number` — u pul emas,
 * satr soni.
 */
import { api } from './api';
import { sumDecimals } from './decimal';
import type { Period } from './period';

/** Har hisobot sarlavhasi — `ReportsService.meta()`. */
export interface ReportMeta {
  readonly generatedAt: string;
  readonly currency: string;
  readonly period?: Period;
  /** Foydalanuvchi qiymat ustunlarini ko'ra oladimi (server hisoblagan). */
  readonly includesValue: boolean;
}

// ---------------------------------------------------------------------------
// 1. Ombor qoldig'i — GET /reports/stock  (stock.view)
// ---------------------------------------------------------------------------

export interface StockRow {
  readonly productId: number;
  readonly sku: string;
  readonly name: string;
  readonly uomId: number;
  readonly warehouseId: number;
  readonly warehouseCode: string;
  readonly onHand: string;
  readonly reserved: string;
  readonly available: string;
  readonly inTransit: string;
  /** FAQAT `stock.value.view` bilan keladi. */
  readonly avgCost?: string;
  /** FAQAT `stock.value.view` bilan keladi. Ombordagi (`onHand`) tovarga tegishli. */
  readonly totalValue?: string;
  /**
   * Yo'ldagi (hali qabul qilinmagan) tovarning puli — FAQAT `stock.value.view`.
   *
   * `inTransit × avgCost` DEB HISOBLAB BO'LMAYDI va shuning uchun u serverdan
   * keladi: yo'ldagi tovar `avgCost` ga umuman kirmagan (u ombordagi tovarning
   * o'rtachasi). Server SHU mahsulotning oxirgi post qilingan qabulidagi
   * to'liq landed tannarxini ishlatadi, u bo'lmasa `avgCost` ga tushadi
   * (`reports.service.ts` → `inTransitUnitCost()`).
   */
  readonly inTransitValue?: string;
}

export interface StockReport {
  readonly meta: ReportMeta;
  readonly rows: readonly StockRow[];
}

export interface StockFilters {
  readonly warehouseId?: number | undefined;
  readonly categoryId?: number | undefined;
}

export function fetchStockReport(filters: StockFilters, signal?: AbortSignal): Promise<StockReport> {
  return api.get<StockReport>('/reports/stock', {
    query: { warehouseId: filters.warehouseId, categoryId: filters.categoryId },
    ...(signal ? { signal } : {}),
  });
}

// ---------------------------------------------------------------------------
// 2. Ombor qiymati — GET /reports/stock-valuation  (stock.value.view)
// ---------------------------------------------------------------------------

export interface StockValuationRow {
  readonly warehouseId: number;
  readonly warehouseCode: string;
  readonly warehouseName: string;
  readonly skuCount: number;
  readonly totalQty: string;
  readonly totalValue: string;
}

export interface StockValuationReport {
  readonly meta: ReportMeta;
  readonly rows: readonly StockValuationRow[];
}

export function fetchStockValuation(
  filters: StockFilters,
  signal?: AbortSignal,
): Promise<StockValuationReport> {
  return api.get<StockValuationReport>('/reports/stock-valuation', {
    query: { warehouseId: filters.warehouseId },
    ...(signal ? { signal } : {}),
  });
}

// ---------------------------------------------------------------------------
// 2b. Bosh sahifa agregati — GET /reports/stock-summary  (stock.view)
// ---------------------------------------------------------------------------

/**
 * ┌──────────────────────────────────────────────────────────────────────┐
 * │ NIMA UCHUN ALOHIDA ENDPOINT                                          │
 * │                                                                      │
 * │ Bosh sahifa bu raqamlarni `GET /reports/stock` ning TO'LIQ ro'yxatini │
 * │ tortib olib, brauzerda sanardi. Jonli bazada ~100 satr, katalog esa  │
 * │ 40 000 SKU ga mo'ljallangan — o'shanda bosh sahifa har ochilganda    │
 * │ o'n minglab satrni tarmoqdan tortadi. Endi javob BITTA qator va uni  │
 * │ server BITTA so'rovda hisoblaydi.                                    │
 * └──────────────────────────────────────────────────────────────────────┘
 *
 * ============================================================================
 * DEYARLI HAMMA MAYDON IXTIYORIY — VA BU ATAYLAB
 * ============================================================================
 * Agregat satrlar yig'indisi, ya'ni satr darajasidagi ruxsat qoidasi bu yerda
 * ham amal qiladi (aks holda maskalangan `onHand`/`inTransit` yig'indi
 * ko'rinishida boshqa eshikdan chiqib ketardi):
 *
 *   `skuCount`, `totalAvailable`                  — HAR DOIM
 *   `totalOnHand`, `totalInTransit`,
 *   `lowStockCount`, `deadStockCount`             — `stock.detail.view`
 *   `totalValue`, `inTransitValue`                — `stock.value.view`
 *
 * Server ruxsat bo'lmasa kalitni `null` QILMAYDI, javobdan BUTUNLAY olib
 * tashlaydi. Jonli tekshirilgan: `sotuvchi@evo.uz` javobida faqat `meta`,
 * `deadStockDays`, `skuCount`, `totalAvailable` bor.
 */
export interface StockSummary {
  readonly meta: ReportMeta;
  /** Harakatsizlik chegarasi — `deadStockCount` qaysi chegarada olingani. */
  readonly deadStockDays: number;
  /** Qoldig'i bor SKU soni (unikal `product_id`). */
  readonly skuCount: number;
  /** `onHand − reserved` yig'indisi. Sotuvchi ham ko'radi. */
  readonly totalAvailable: string;
  readonly totalOnHand?: string;
  /** Yo'ldagi (konteynerdagi) jami miqdor. */
  readonly totalInTransit?: string;
  /** Qayta buyurtma nuqtasidan past pozitsiyalar soni. */
  readonly lowStockCount?: number;
  /** `deadStockDays` kundan beri harakatsiz pozitsiyalar soni. */
  readonly deadStockCount?: number;
  readonly totalValue?: string;
  /** Yo'ldagi tovarning puli — hisob serverda (`StockRow.inTransitValue` izohi). */
  readonly inTransitValue?: string;
}

export function fetchStockSummary(
  deadStockDays?: number,
  signal?: AbortSignal,
): Promise<StockSummary> {
  return api.get<StockSummary>('/reports/stock-summary', {
    query: { deadStockDays },
    ...(signal ? { signal } : {}),
  });
}

// ---------------------------------------------------------------------------
// 3. Savdo — GET /reports/sales  (so.view)
// ---------------------------------------------------------------------------

export const SALES_GROUP_BY = ['day', 'customer', 'product', 'agent'] as const;
export type SalesGroupBy = (typeof SALES_GROUP_BY)[number];

/**
 * To'rt kesimning UMUMIY shakli.
 *
 * Server har kesim uchun ALOHIDA `select()` yozadi va ular biroz farq qiladi:
 *   • `day`      → key = sana satri, label = o'sha sana
 *   • `customer` → key = mijoz id, label = mijoz nomi
 *   • `product`  → key = mahsulot id, label = SKU, name = nomi
 *   • `agent`    → key = salespersonId, label = xodimning `users.full_name` i
 *
 * `agent` kesimida `label` ENDI BOR (jonli tekshirilgan:
 * `{"key":3,"label":"Tizim administratori",...}`). U `null` bo'lishi MUMKIN —
 * `salesperson_id` bo'sh hujjat (`leftJoin`), ya'ni «biriktirilmagan savdo».
 *
 * `name` esa faqat `product` kesimida bor, shuning uchun ikkalasi ham
 * ixtiyoriy: server maydonni `null` qilmaydi, javobga UMUMAN qo'shmaydi.
 */
export interface SalesRow {
  readonly key: string | number | null;
  readonly label?: string | null;
  readonly name?: string | null;
  readonly revenue: string;
  readonly quantity: string;
  readonly lineCount: number;
}

export interface SalesReport {
  readonly meta: ReportMeta;
  readonly groupBy: SalesGroupBy;
  readonly rows: readonly SalesRow[];
}

export function fetchSalesReport(
  period: Period,
  groupBy: SalesGroupBy,
  signal?: AbortSignal,
): Promise<SalesReport> {
  return api.get<SalesReport>('/reports/sales', {
    query: { from: period.from, to: period.to, groupBy },
    ...(signal ? { signal } : {}),
  });
}

// ---------------------------------------------------------------------------
// 4-5. Yalpi foyda — GET /reports/gross-profit  (so.margin.view)
// ---------------------------------------------------------------------------

export const PROFIT_BY = ['product', 'customer'] as const;
export type ProfitBy = (typeof PROFIT_BY)[number];

export interface GrossProfitRow {
  readonly key: number | null;
  /** `product` kesimida SKU, `customer` kesimida mijoz kodi. */
  readonly label: string | null;
  readonly name: string | null;
  readonly quantity: string;
  readonly revenue: string;
  readonly cogs: string;
  /**
   * Bonus satrlar tannarxi. Tushumga 0 qo'shadi, lekin tannarxni TO'LIQ ayiradi —
   * ya'ni bu yashirin chegirma (reports.service.ts §12A.3 izohi).
   */
  /** `so.margin.view` bo'lmasa javobda YO'Q — interceptor kalitni butunlay olib tashlaydi. */
  readonly bonusCost?: string;
  readonly grossProfit: string;
  /** `round(((revenue - cogs) / nullif(revenue, 0)) * 100, 2)` — nol tushumda `null`. */
  readonly marginPct: string | null;
}

export interface GrossProfitReport {
  readonly meta: ReportMeta;
  readonly by: ProfitBy;
  readonly rows: readonly GrossProfitRow[];
}

export function fetchGrossProfit(
  period: Period,
  by: ProfitBy,
  signal?: AbortSignal,
): Promise<GrossProfitReport> {
  return api.get<GrossProfitReport>('/reports/gross-profit', {
    query: { from: period.from, to: period.to, by },
    ...(signal ? { signal } : {}),
  });
}

// ---------------------------------------------------------------------------
// 6. AR aging — GET /reports/ar-aging  (ar.view)
// ---------------------------------------------------------------------------

export interface ArAgingRow {
  readonly customerId: number;
  readonly customerCode: string;
  readonly customerName: string;
  readonly creditLimit: string | null;
  /** Muddati kelmagan qarz — kechikish EMAS. */
  readonly notDue: string;
  readonly d0_30: string;
  readonly d31_60: string;
  readonly d61_90: string;
  readonly d90plus: string;
  readonly total: string;
  readonly oldestDueDate: string | null;
  readonly invoiceCount: number;
}

export interface ArAgingReport {
  readonly meta: ReportMeta;
  readonly asOf: string;
  readonly rows: readonly ArAgingRow[];
}

// ---------------------------------------------------------------------------
// 6a. Mijoz intizomi — GET /reports/customer-discipline  (ar.view)
// ---------------------------------------------------------------------------

/**
 * Bitta oyning qatori: harakat + reja/fakt.
 *
 * Zanjir yopiladi va u SERVERDA yopiladi:
 *   `openingDebt + sales − payment − returned − discount − writeOff = closingDebt`
 * keyingi oyning `openingDebt` i esa shu oyning `closingDebt` i.
 *
 * Foizlar `'77.90'` ko'rinishida (satr, ikki kasr xona) keladi — brauzerda
 * ular QAYTA HISOBLANMAYDI, faqat chiziladi (`lib/reports.ts` qoidasi).
 */
export interface DisciplineRow {
  /** `YYYY-MM` yoki `YYYY-MM#1` (hafta). */
  readonly month: string;
  /** Hafta raqami (1..4) yoki `null` — oylik qator. */
  readonly week: number | null;
  /** Qatorning haqiqiy sana oralig'i. */
  readonly periodFrom: string;
  readonly periodTo: string;
  readonly openingDebt: string;
  readonly sales: string;
  readonly payment: string;
  readonly returned: string;
  readonly discount: string;
  readonly writeOff: string;
  readonly closingDebt: string;
  /** `openingDebt + sales` — «to'lov / (qoldiq + xarid)» nisbatining maxraji. */
  readonly collectionBase: string;

  readonly planPurchasePct: string;
  /** O'tgan oy qoldig'idan hisoblangan minimal xarid summasi. */
  readonly planPurchaseAmount: string;
  readonly planPaymentPct: string;
  readonly planCollectionPct: string;
  /** (qoldiq + xarid) dan hisoblangan minimal to'lov summasi. */
  readonly planCollectionAmount: string;

  readonly factPurchasePct: string;
  readonly factPaymentPct: string;
  readonly factCollectionPct: string;
}

export interface DisciplineReport {
  readonly meta: ReportMeta;
  /** `null` — mijoz topilmadi yoki ko'lamdan tashqarida. */
  readonly customer: { readonly id: number; readonly code: string; readonly name: string } | null;
  readonly rows: readonly DisciplineRow[];
}

/**
 * Umumiy ro'yxatning bitta qatori — BITTA mijoz, BITTA oy.
 *
 * `failed` — bajarilmagan nisbatlar soni (0..3). Ro'yxat aynan shu bo'yicha
 * saralanadi: xodim qidirmaydi, eng yomonlari tepada turadi.
 */
export interface DisciplineOverviewRow {
  readonly customerId: number;
  readonly customerCode: string;
  readonly customerName: string;
  readonly openingDebt: string;
  readonly sales: string;
  readonly payment: string;
  readonly credited: string;
  readonly closingDebt: string;
  readonly planPurchasePct: string;
  readonly planPaymentPct: string;
  readonly planCollectionPct: string;
  readonly factPurchasePct: string;
  readonly factPaymentPct: string;
  readonly factCollectionPct: string;
  readonly collectionBase: string;
  readonly failed: number;
  /** Oxirgi to'lov sanasi (oy oxirigacha) yoki `null` — hech qachon to'lamagan. */
  readonly lastPaymentDate: string | null;
  /** Shu oyda to'lanishi KERAK bo'lgan summa — yuk olmagan bo'lsa ham. */
  readonly planCollectionAmount: string;
  /** Rejadan yetishmayotgani. Ortiqcha to'laganda nol (manfiy emas). */
  readonly shortfall: string;
}

export interface DisciplineOverviewReport {
  readonly meta: ReportMeta;
  /** `YYYY-MM`. */
  readonly month: string;
  readonly norm: {
    readonly purchasePct: string;
    readonly paymentPct: string;
    readonly collectionPct: string;
  };
  /**
   * Ustun yig'indilari — SERVERDA hisoblangan.
   *
   * Brauzerda qo'shish `Number()` ga olib kelardi (loyihada taqiqlangan
   * naqsh) va sahifalash qo'shilgan kuni yig'indi jimgina faqat ko'rinib
   * turgan qatorlarni sanardi.
   */
  readonly totals: {
    readonly openingDebt: string;
    readonly sales: string;
    readonly payment: string;
    readonly closingDebt: string;
    readonly planCollectionAmount: string;
    readonly shortfall: string;
    /** Pul bo'yicha rejadan orqada qolganlar — undirish ro'yxati. */
    readonly behindCount: number;
    /** Nisbatlardan bittasi bo'lsa ham og'ganlar — kengroq to'plam. */
    readonly failedCount: number;
  };
  readonly rows: readonly DisciplineOverviewRow[];
}

/**
 * Kompaniya standarti — 12 oylik normativ.
 *
 * Mijoz kartochkasi buni «bo'sh katak nimani bildiradi» degan savolga javob
 * sifatida ko'rsatadi. `settings` dan emas, hisobotdan olinadi: sozlamalarni
 * o'qish faqat adminda, kartochkani esa menejer ochadi.
 */
export interface DisciplineNormDefaults {
  readonly meta: ReportMeta;
  /** Kalit — oy raqami (`'1'`..`'12'`). */
  readonly norm: Readonly<
    Record<string, { purchasePct: string; paymentPct: string; collectionPct: string }>
  >;
}

export function fetchDisciplineNormDefaults(
  signal?: AbortSignal,
): Promise<DisciplineNormDefaults> {
  return api.get<DisciplineNormDefaults>('/reports/discipline-norm', {
    ...(signal ? { signal } : {}),
  });
}

export function fetchDisciplineOverview(
  month: string,
  signal?: AbortSignal,
): Promise<DisciplineOverviewReport> {
  return api.get<DisciplineOverviewReport>('/reports/discipline-overview', {
    query: { month },
    ...(signal ? { signal } : {}),
  });
}

export function fetchCustomerDiscipline(
  params: { customerId: number; from: string; to: string; bucket?: 'week' },
  signal?: AbortSignal,
): Promise<DisciplineReport> {
  return api.get<DisciplineReport>('/reports/customer-discipline', {
    query: {
      customerId: params.customerId,
      from: params.from,
      to: params.to,
      bucket: params.bucket,
    },
    ...(signal ? { signal } : {}),
  });
}

/**
 * Muddati o'tgan qarz — to'rtta guruh yig'indisi. `notDue` KIRMAYDI.
 *
 * ┌──────────────────────────────────────────────────────────────────────────┐
 * │ NIMA UCHUN BU YERDA, EKRANDA EMAS                                        │
 * │                                                                          │
 * │ Avval u `routes/reports/ar-aging.tsx` da yashardi va u yerdan eksport    │
 * │ qilinardi. Ikkinchi ekran (`/receivables`) paydo bo'lgach, u EKRANDAN    │
 * │ ekranga import qilinishi kerak bo'lardi — bu esa butun hisobot           │
 * │ modulini (jadval, eksport, sana tanlagich) ikkinchi ekranning            │
 * │ chunk'iga tortib kelardi.                                                │
 * │                                                                          │
 * │ Formula esa MA'LUMOT qoidasi, ko'rinish emas: «kechikkan = to'rtta       │
 * │ guruh yig'indisi, muddati kelmagani kirmaydi». Uning o'rni ma'lumot      │
 * │ qatlamida.                                                               │
 * └──────────────────────────────────────────────────────────────────────────┘
 *
 * Serverda bunday ustun YO'Q (`arAging()` faqat guruhlarni va `total` ni
 * beradi). Yig'indi `sumDecimals` (BigInt) bilan — `parseFloat` TAQIQLANGAN:
 * 5 000 satrli hisobotda float xatosi buxgalteriya bilan tiyinlab farq beradi.
 */
export function overdueAmount(row: ArAgingRow): string {
  return sumDecimals([row.d0_30, row.d31_60, row.d61_90, row.d90plus]) ?? '';
}

export function fetchArAging(asOf: string | undefined, signal?: AbortSignal): Promise<ArAgingReport> {
  return api.get<ArAgingReport>('/reports/ar-aging', {
    query: { asOf },
    ...(signal ? { signal } : {}),
  });
}

// ---------------------------------------------------------------------------
// 6b. AP aging — GET /reports/ap-aging  (ap.view)
// ---------------------------------------------------------------------------

/**
 * Ta'minotchiga qarz.
 *
 * ┌──────────────────────────────────────────────────────────────────────────┐
 * │ ENDPOINT BOR — `config/menu.ts` DAGI ESKI IZOH XATO EDI                  │
 * │                                                                          │
 * │ Menyudagi `rpt-ap-aging` bandi «`reports.controller.ts` da endpoint      │
 * │ YO'Q» deb izohlangan va shu sababdan `route: null` turgan edi. Bu        │
 * │ noto'g'ri: `@Get('ap-aging') @RequirePermissions('ap.view')` o'sha       │
 * │ faylda bor va u ISHLAYDI.                                                │
 * │                                                                          │
 * │ JONLI TEKSHIRILGAN (bugun): admin → 200, ikkita ta'minotchi qatori;      │
 * │ `ombor@evo.uz` va `sotuvchi@evo.uz` → 403.                               │
 * └──────────────────────────────────────────────────────────────────────────┘
 *
 * `ar-aging` dan farqi ikkita va ikkalasi ham ma'noli:
 *
 *   1. YOSH GURUHLARI YO'Q. Mijoz qarzi muddat bo'yicha eskiradi; ta'minotchi
 *      oldidagi majburiyat esa xarid buyurtmasining holatiga bog'liq va unda
 *      «90+ kun» degan tushuncha yo'q — buyurtma yo ochiq, yo yopilgan.
 *   2. AVANS ALOHIDA USTUN. Import siklida 30% deposit 4 oy «osilgan» turadi
 *      — bu aylanmadan chiqib ketgan pul va u qarzdan boshqa narsa.
 *
 * Maydon nomlari `ap` prefiksi bilan (`apOutstandingBase`) — bu SERVER
 * qarori: maskalash MAYDON NOMI bo'yicha ishlaydi va prefikssiz nom mijoz
 * qarzi (`ar.view`) bilan bitta kalitga tushib qolardi.
 */
export interface ApAgingRow {
  readonly supplierId: number;
  readonly supplierCode: string;
  readonly supplierName: string;
  /** Ta'minotchining hisob valyutasi (odatda CNY). */
  readonly currency: string;
  readonly paymentTermDays: number;
  readonly openPoCount: number;
  /** Buyurtma qilingan umumiy summa, USD. */
  readonly apOrderedBase: string;
  readonly apPaidBase: string;
  /** Qarz = buyurtma − to'langan, USD. */
  readonly apOutstandingBase: string;
  /** Biriktirilmagan chiqim to'lovi = ochiq avans, USD. */
  readonly apOpenAdvanceBase: string;
  readonly oldestAdvanceDate: string | null;
  /** Avans necha kundan beri yopilmagan. `null` — avans yo'q. */
  readonly advanceAgeDays: number | null;
  readonly nearestExpectedDate: string | null;

  /*
   * ═══════════════════════════════════════════════════════════════════════
   * HUJJAT BO'YICHA QARZ — BUYURTMA BO'YICHASIDAN AYRIM
   * ═══════════════════════════════════════════════════════════════════════
   * `apOutstandingBase` (yuqorida) BUYURTMA savoliga javob beradi: «zavodga
   * qancha buyurtma berdik va qanchasini to'ladik» — pul rejasi.
   *
   * Quyidagilar HUJJAT savoliga: «zavod bizga qancha invoys yozdi va
   * qanchasi to'lanmagan». Bu daftardagi `ap` hisobi bilan bir xil ma'noga
   * ega, chunki ikkalasi ham faqat TAN OLINGAN (`received`) invoysdan
   * tug'iladi.
   *
   * Ular BIR XIL RAQAM EMAS va bo'lishi ham shart emas: buyurtma berilgan,
   * lekin invoysi hali kelmagan konteyner birinchisida bor, ikkinchisida
   * yo'q. Import siklida bu farq oylab davom etadi.
   */

  /** Tan olingan invoyslar jami, USD. */
  readonly apInvoicedBase: string;
  /**
   * To'lanmagan qismi, USD.
   *
   * To'lov ta'minotchi invoysiga BIRIKTIRILMAYDI (bazada bunday imkoniyat
   * yo'q — `ck_payment_allocations_target_table` faqat `invoice` va
   * `purchase_order` ga ruxsat beradi), shuning uchun server FIFO taxminini
   * qo'llaydi: to'lov eng eski invoysdan boshlab qoplanadi. Bu TAXMIN va u
   * hisobot izohida ochiq aytiladi.
   */
  readonly apInvoiceOpenBase: string;
  readonly apOpenInvoiceCount: number;
  /** Eng eski ochiq invoysning muddati. `null` — ochiq invoys yo'q. */
  readonly apOldestDueDate: string | null;
  /** MANFIY = muddat hali kelmagan (`invoices` dagi `daysOverdue` bilan bir xil qoida). */
  readonly apOverdueDays: number | null;

  /** Muddat guruhlari — `ar-aging` bilan AYNAN bir xil oynalar. */
  readonly apNotDue: string;
  readonly apD0_30: string;
  readonly apD31_60: string;
  readonly apD61_90: string;
  readonly apD90Plus: string;
}

export interface ApAgingReport {
  readonly meta: ReportMeta;
  readonly rows: readonly ApAgingRow[];
}

/** `asOf` YO'Q: endpoint parametr olmaydi — qarz JORIY holat kesimi. */
export function fetchApAging(signal?: AbortSignal): Promise<ApAgingReport> {
  return api.get<ApAgingReport>('/reports/ap-aging', { ...(signal ? { signal } : {}) });
}

// ---------------------------------------------------------------------------
// 7. Pul harakati — GET /reports/cash-flow  (cashaccount.view)
// ---------------------------------------------------------------------------

export interface CashFlowRow {
  readonly date: string;
  readonly categoryCode: string | null;
  readonly cashIn: string;
  readonly cashOut: string;
  readonly net: string;
}

/**
 * Ichki o'tkazma — kassadan bankka va h.k.
 *
 * `rows` ga KIRMAYDI: kompaniyaning pul miqdori bunday harakatda
 * o'zgarmaydi va uni aylanmaga qo'shish tushumni SOXTA oshirardi
 * (`reports.service.ts` `cashFlow()` ramkasi). Lekin u YO'QOTILMAYDI ham:
 * «kassadan bankka qancha o'tdi» — moliyachining haqiqiy savoli.
 *
 * `amount` faqat CHIQIM oyog'idan; kirim oyog'i o'sha summani takrorlaydi.
 */
export interface CashFlowTransferRow {
  readonly date: string;
  readonly amount: string;
  readonly legCount: number;
}

export interface CashFlowReport {
  readonly meta: ReportMeta;
  readonly rows: readonly CashFlowRow[];
  readonly internalTransfers: readonly CashFlowTransferRow[];
}

export function fetchCashFlow(period: Period, signal?: AbortSignal): Promise<CashFlowReport> {
  return api.get<CashFlowReport>('/reports/cash-flow', {
    query: { from: period.from, to: period.to },
    ...(signal ? { signal } : {}),
  });
}

// ---------------------------------------------------------------------------
// 8. Sekin harakatlanuvchi tovar — GET /reports/dead-stock  (stock.view)
// ---------------------------------------------------------------------------

export interface DeadStockRow {
  readonly productId: number;
  readonly sku: string;
  readonly name: string;
  readonly warehouseCode: string;
  readonly onHand: string;
  readonly lastOutboundDate: string | null;
  /** Harakatsiz kunlar. Umuman chiqim bo'lmagan bo'lsa server `9999` qaytaradi. */
  readonly daysIdle: number;
  /** FAQAT `stock.value.view` bilan keladi. */
  readonly totalValue?: string;
}

export interface DeadStockReport {
  readonly meta: ReportMeta;
  readonly thresholdDays: number;
  readonly rows: readonly DeadStockRow[];
}

export function fetchDeadStock(days: number, signal?: AbortSignal): Promise<DeadStockReport> {
  return api.get<DeadStockReport>('/reports/dead-stock', {
    query: { days },
    ...(signal ? { signal } : {}),
  });
}

// ---------------------------------------------------------------------------
// 9. SKU landed cost tarkibi — GET /reports/landed-cost/:shipmentId  (landedcost.view)
// ---------------------------------------------------------------------------

/**
 * Bitta SATR = bitta SKU ga tushgan BITTA xarajat turi.
 *
 * Ya'ni bir SKU javobda bir necha marta uchraydi (navlon, bojxona, sertifikat…)
 * va SKU darajasidagi ko'rsatkichlar (`purchaseUnitCost`, `landedUnitCost`,
 * `totalUnitCost`) har satrda TAKRORLANADI. Ekran shu satrlarni SKU bo'yicha
 * guruhlaydi — «nega bu panel $14.12 turadi?» degan savolga javob aynan
 * shu guruhlashdan chiqadi (LANDED_COST §9).
 */
export interface LandedCostRow {
  readonly goodsReceiptLineId: number;
  readonly productId: number;
  readonly sku: string;
  readonly quantity: string;
  /** Faqat tovar narxi (CNY → USD, qabul kursi bo'yicha). */
  readonly purchaseUnitCost: string;
  /** Taqsimlangan xarajatlar / miqdor. */
  readonly landedUnitCost: string;
  /** `purchase + landed` — ombor harakatiga aynan shu tushadi. */
  readonly totalUnitCost: string;
  readonly costTypeCode: string | null;
  readonly costName: string | null;
  readonly allocMethod: string;
  readonly allocRatio: string;
  /** Shu xarajat satridan shu SKU ga tushgan summa. */
  readonly allocatedBase: string;
}

export interface LandedCostReport {
  readonly meta: ReportMeta;
  readonly shipmentId: number;
  readonly rows: readonly LandedCostRow[];
}

export function fetchLandedCost(shipmentId: number, signal?: AbortSignal): Promise<LandedCostReport> {
  return api.get<LandedCostReport>(`/reports/landed-cost/${shipmentId}`, {
    ...(signal ? { signal } : {}),
  });
}

// ---------------------------------------------------------------------------
// 10. Konteyner rentabelligi — GET /reports/shipment-profitability  (landedcost.view)
// ---------------------------------------------------------------------------

/**
 * GAP_ANALYSIS §A3 — import biznesining asosiy savoli.
 *
 * `landedPct` = taqsimlangan XARAJATLAR / TOVAR narxi × 100.
 * `goods_receipt_lines` da uch ustun bor va ularning ma'nosi
 * (procurement.ts:409) shunday:
 *
 *   purchaseUnitCost — faqat tovar narxi
 *   landedUnitCost   — taqsimlangan xarajatlar / miqdor
 *   totalUnitCost    — purchase + landed
 *
 * Ya'ni `landedPct = 109%` degani «xarajat tovar narxidan ham ko'proq».
 * Bu — xitoy invoysi bo'yicha narx aytishga o'rgangan odam uchun butun
 * tizimdagi eng kutilmagan raqam, shuning uchun ekranda u eng katta.
 */
export interface ShipmentProfitRow {
  readonly shipmentId: number;
  readonly docNo: string;
  readonly containerNo: string | null;
  readonly eta: string | null;
  readonly ata: string | null;
  /** `ata - eta` kunlarda. Musbat — kechikdi, manfiy — erta keldi. */
  readonly delayDays: number;
  readonly lcStatus: string | null;
  readonly skuCount: number;
  readonly receivedQty: string;
  readonly shortageQty: string;
  /*
   * Uchtasi ham `landedcost.view` / `product.cost.view` ostida. Ruxsat
   * bo'lmasa `MaskingInterceptor` kalitni `null` qilmaydi, BUTUNLAY olib
   * tashlaydi — shuning uchun tipda ixtiyoriy.
   */
  readonly purchaseValue?: string;
  readonly landedValue?: string;
  readonly totalCost?: string;
  readonly landedPct?: string | null;
}

export interface ShipmentProfitReport {
  readonly meta: ReportMeta;
  readonly rows: readonly ShipmentProfitRow[];
}

export function fetchShipmentProfitability(
  shipmentId: number | undefined,
  signal?: AbortSignal,
): Promise<ShipmentProfitReport> {
  return api.get<ShipmentProfitReport>('/reports/shipment-profitability', {
    query: { shipmentId },
    ...(signal ? { signal } : {}),
  });
}

// ---------------------------------------------------------------------------
// 11. Qachon buyurtma berish kerak — GAP_ANALYSIS §A4
//     GET /reports/reorder  (po.view)
// ---------------------------------------------------------------------------

/**
 * ┌──────────────────────────────────────────────────────────────────────┐
 * │ LEAD TIME 120-150 KUN                                                │
 * │                                                                      │
 * │ «Tovar tugadi → buyurtma beraman» qoidasi 4 oylik uzilish demak.     │
 * │ Qaror tovar tugashidan ANCHA oldin qabul qilinishi kerak va uni      │
 * │ faqat hisob-kitob ko'rsatadi:                                        │
 * │                                                                      │
 * │   kunlik_o'rtacha = oxirgi N kun SOTUV chiqimi / N                   │
 * │   qolgan_kunlar   = (qoldiq + yo'lda) / kunlik_o'rtacha              │
 * │   qolgan_kunlar < (lead_time + xavfsizlik) → BUYURTMA BERING         │
 * │                                                                      │
 * │ Transfer va namuna chiqimi TALABGA kirmaydi — ular sotuv emas.       │
 * └──────────────────────────────────────────────────────────────────────┘
 */
export const REORDER_STATUSES = ['order_now', 'ok', 'no_demand'] as const;
export type ReorderStatus = (typeof REORDER_STATUSES)[number];

export interface ReorderRow {
  readonly productId: number;
  readonly sku: string;
  readonly name: string;
  readonly onHand: string;
  /** Yo'ldagi (konteynerdagi) miqdor — u ham qoplamaga kiradi. */
  readonly inTransit: string;
  readonly soldInPeriod: string;
  readonly dailyAverage: string;
  /**
   * Ta'minotchining HAQIQIY lead time'i, bo'lmasa so'rovdagi standart.
   *
   * `supplier.view` bo'lmasa hisobot uni UMUMAN so'ramaydi (proyeksiya) va
   * interceptor ham olib tashlaydi — ikki to'siq, demak tipda ixtiyoriy.
   */
  readonly leadTimeDays?: number;
  /** Talab bo'lmasa `null` — nolga bo'lish natijasi emas, «hisoblab bo'lmaydi». */
  readonly daysOfCover: string | null;
  readonly status: ReorderStatus;
  readonly suggestedQty: string;
}

export interface ReorderReport {
  readonly meta: ReportMeta;
  readonly params: {
    readonly lookbackDays: number;
    readonly safetyDays: number;
    readonly defaultLeadTimeDays: number;
  };
  readonly summary: {
    readonly orderNow: number;
    readonly total: number;
    /** Serverning O'ZBEKCHA xulosasi — ekran uni qayta yozmaydi. */
    readonly message: string;
  };
  readonly rows: readonly ReorderRow[];
}

export interface ReorderParams {
  readonly lookbackDays: number;
  readonly safetyDays: number;
  readonly leadTimeDays: number;
}

/** Server qabul qiladigan oraliqlar (`ReorderSchema`) — brauzer ham shu chegarada. */
export const REORDER_DEFAULTS: ReorderParams = {
  lookbackDays: 90,
  safetyDays: 30,
  leadTimeDays: 130,
};

export function fetchReorder(params: ReorderParams, signal?: AbortSignal): Promise<ReorderReport> {
  return api.get<ReorderReport>('/reports/reorder', {
    query: {
      lookbackDays: params.lookbackDays,
      safetyDays: params.safetyDays,
      leadTimeDays: params.leadTimeDays,
    },
    ...(signal ? { signal } : {}),
  });
}

// ===========================================================================
// TAHLIL HISOBOTLARI (TZ v4.0 §28 / CR-007, v8.0 §38–39)
// ===========================================================================

/**
 * ┌──────────────────────────────────────────────────────────────────────────┐
 * │ `null` — HISOBLANMADI, NOL EMAS                                          │
 * │                                                                          │
 * │ Bu blokdagi deyarli har ko'rsatkich `null` bo'lishi mumkin va u HAR      │
 * │ DOIM `reason` bilan birga keladi. Server ataylab shunday qiladi: bir     │
 * │ marta xarid qilgan mijozning «chastotasi» yo'q, ikki haftalik tarixda    │
 * │ «barqarorlik» o'lchanmaydi. Nol ball esa «yomon» degan YOLG'ON ma'no     │
 * │ berardi va o'sha satr ishlash ro'yxatidan chiqib ketardi.                │
 * │                                                                          │
 * │ Shuning uchun ekranda `null` HECH QACHON `0` bilan almashtirilmaydi —    │
 * │ u `—` bo'lib chiqadi va sababi izohda ko'rsatiladi.                      │
 * └──────────────────────────────────────────────────────────────────────────┘
 */
export const INSUFFICIENT_REASONS = [
  'no_data',
  'single_purchase',
  'too_few_periods',
  'too_few_peers',
  'short_history',
  'zero_base',
  'ambiguous_source',
] as const;
export type InsufficientReason = (typeof INSUFFICIENT_REASONS)[number];

// ---------------------------------------------------------------------------
// 12. ABC — GET /reports/abc  (stock.value.view)
// ---------------------------------------------------------------------------

export type AbcClass = 'A' | 'B' | 'C';

export interface AbcRow {
  readonly productId: number;
  readonly sku: string;
  readonly name: string;
  readonly quantity: string;
  readonly lineCount: number;
  readonly revenue: string;
  /** FAQAT `so.margin.view` bilan keladi (server proyeksiyasi). */
  readonly cogs?: string;
  readonly grossProfit?: string;
  readonly marginPct?: string | null;
  /** Shu tovarda BUGUN qotib turgan pul — davrga bog'liq emas. */
  readonly stockValue: string;
  readonly sharePct: string;
  readonly cumulativePct: string;
  readonly rank: number;
  readonly abcClass: AbcClass | null;
  readonly abcReason: InsufficientReason | null;
}

export interface AbcThresholds {
  readonly aPct: string;
  readonly bPct: string;
  /** `100 − aPct − bPct` — server hisoblaydi, ekran qayta hisoblamaydi. */
  readonly cPct: string;
}

export interface AbcReport {
  readonly meta: ReportMeta;
  readonly thresholds: AbcThresholds;
  /** Formulaning O'ZBEKCHA ta'rifi — «nega bu tovar C da?» savoliga javob. */
  readonly formula: string;
  readonly summary: Record<AbcClass, { readonly count: number; readonly revenue: string }>;
  readonly rows: readonly AbcRow[];
}

export interface AbcParams {
  readonly aPct: string;
  readonly bPct: string;
}

/** Server sxemasidagi standart chegaralar (`ABC_DEFAULT_THRESHOLDS`). */
export const ABC_DEFAULTS: AbcParams = { aPct: '80', bPct: '15' };

export function fetchAbc(
  period: Period,
  params: AbcParams,
  signal?: AbortSignal,
): Promise<AbcReport> {
  return api.get<AbcReport>('/reports/abc', {
    query: { from: period.from, to: period.to, aPct: params.aPct, bPct: params.bPct },
    ...(signal ? { signal } : {}),
  });
}

// ---------------------------------------------------------------------------
// 13. XYZ — GET /reports/xyz  (stock.detail.view)
// ---------------------------------------------------------------------------

export type XyzClass = 'X' | 'Y' | 'Z';
export const XYZ_BUCKETS = ['week', 'month'] as const;
export type XyzBucket = (typeof XYZ_BUCKETS)[number];

export interface XyzRow {
  readonly productId: number;
  readonly sku: string;
  readonly name: string;
  /** Kuzatilgan davrlar soni — NOL talabli davrlar ham kiradi. */
  readonly periodCount: number;
  /** Talab BO'LGAN davrlar soni. */
  readonly activePeriodCount: number;
  readonly totalQty: string;
  readonly meanQty: string;
  readonly stdDevQty: string;
  /** Variatsiya koeffitsienti, %. `null` — o'lchanmadi. */
  readonly cvPct: string | null;
  readonly xyzClass: XyzClass | null;
  readonly reason: InsufficientReason | null;
  /**
   * FAQAT `stock.value.view` bilan keladi.
   *
   * ABC tushumga tayanadi, ya'ni omborchiga yopiq. Server ruxsat bo'lmasa
   * ustunni so'rovga UMUMAN kiritmaydi (`analytics.service.ts` ramkasi) —
   * shuning uchun tipda ixtiyoriy.
   */
  readonly abcClass?: AbcClass | null;
  /** `AX`, `CZ` — ikkala sinf ham bor bo'lsagina. */
  readonly combined?: string | null;
}

export interface XyzReport {
  readonly meta: ReportMeta;
  readonly params: {
    readonly lookbackDays: number;
    readonly bucket: XyzBucket;
    readonly minPeriods: number;
    readonly xPct: string;
    readonly yPct: string;
  };
  readonly formula: string;
  readonly summary: {
    readonly total: number;
    readonly insufficient: number;
    readonly message: string;
  };
  readonly rows: readonly XyzRow[];
}

export interface XyzParams {
  readonly lookbackDays: number;
  readonly bucket: XyzBucket;
  readonly xPct: string;
  readonly yPct: string;
  readonly minPeriods: number;
}

export const XYZ_DEFAULTS: XyzParams = {
  lookbackDays: 180,
  bucket: 'week',
  xPct: '10',
  yPct: '25',
  minPeriods: 6,
};

export function fetchXyz(params: XyzParams, signal?: AbortSignal): Promise<XyzReport> {
  return api.get<XyzReport>('/reports/xyz', {
    query: {
      lookbackDays: params.lookbackDays,
      bucket: params.bucket,
      xPct: params.xPct,
      yPct: params.yPct,
      minPeriods: params.minPeriods,
    },
    ...(signal ? { signal } : {}),
  });
}

// ---------------------------------------------------------------------------
// 14. RFM — GET /reports/rfm  (customer.view + report.sales.view)
// ---------------------------------------------------------------------------

export const RFM_SEGMENTS = [
  'champion',
  'loyal',
  'potential',
  'at_risk',
  'lost',
  'needs_attention',
] as const;
export type RfmSegment = (typeof RFM_SEGMENTS)[number];

export interface RfmRow {
  readonly customerId: number;
  readonly customerCode: string;
  readonly customerName: string;
  readonly customerType: string;
  readonly lastPurchaseDate: string | null;
  readonly firstPurchaseDate: string | null;
  /** Oxirgi xariddan beri o'tgan kunlar. */
  readonly recencyDays: number;
  /** Buyurtmalar soni (yuk xatlari soni EMAS — server izohi). */
  readonly purchaseCount: number;
  readonly monetary: string;
  readonly recencyScore: number | null;
  /** Bitta xaridli mijozda `null` — chastota o'lchanmaydi. */
  readonly frequencyScore: number | null;
  readonly monetaryScore: number | null;
  /** «535» — uchala ball ham bor bo'lsagina. */
  readonly rfmCode: string | null;
  readonly segment: RfmSegment | null;
  readonly reason: InsufficientReason | null;
}

export interface RfmReport {
  readonly meta: ReportMeta;
  readonly formula: string;
  readonly params: { readonly minPeers: number };
  readonly summary: {
    readonly total: number;
    readonly singlePurchase: number;
    readonly message: string;
  };
  readonly rows: readonly RfmRow[];
}

export function fetchRfm(period: Period, signal?: AbortSignal): Promise<RfmReport> {
  return api.get<RfmReport>('/reports/rfm', {
    query: { from: period.from, to: period.to },
    ...(signal ? { signal } : {}),
  });
}

// ---------------------------------------------------------------------------
// 15. LTV — GET /reports/ltv  (so.margin.view)
// ---------------------------------------------------------------------------

export interface LtvRow {
  readonly customerId: number;
  readonly customerCode: string;
  readonly customerName: string;
  readonly firstOrderDate: string | null;
  readonly lastOrderDate: string | null;
  /** Birinchi xariddan BUGUNGACHA (oxirgi xaridgacha emas — server izohi). */
  readonly lifespanDays: number;
  readonly orderCount: number;
  readonly revenue: string;
  readonly cogs: string;
  readonly grossProfit: string;
  readonly returnsBase: string;
  /** `grossProfit − returnsBase`. */
  readonly ltv: string;
  readonly avgOrderValue: string;
  /** Tarix qisqa bo'lsa `null` — ekstrapolyatsiya qilinmaydi. */
  readonly monthlyValue: string | null;
  readonly monthlyValueReason: InsufficientReason | null;
}

export interface LtvReport {
  readonly meta: ReportMeta;
  readonly formula: string;
  readonly params: { readonly minLifespanDays: number };
  readonly summary: {
    readonly total: number;
    readonly shortHistory: number;
    readonly message: string;
  };
  readonly rows: readonly LtvRow[];
}

export const LTV_DEFAULT_MIN_LIFESPAN_DAYS = 30;

export function fetchLtv(minLifespanDays: number, signal?: AbortSignal): Promise<LtvReport> {
  return api.get<LtvReport>('/reports/ltv', {
    query: { minLifespanDays },
    ...(signal ? { signal } : {}),
  });
}

// ---------------------------------------------------------------------------
// 16. Ta'minotchi ko'rsatkichi — GET /reports/supplier-scorecard  (supplier.view)
// ---------------------------------------------------------------------------

export interface SupplierScoreRow {
  readonly supplierId: number;
  readonly supplierCode: string;
  readonly supplierName: string;
  readonly country: string | null;
  /** Zavod E'LON QILGAN muddat (`suppliers.lead_time_days`). */
  readonly declaredLeadTimeDays: number;
  readonly poCount: number;
  readonly receivedPoCount: number;
  /** Buyurtmada VA'DA qilingan muddat, o'rtacha kun. */
  readonly promisedLeadDays: string | null;
  /** HAQIQIY muddat: birinchi post qilingan qabulgacha, o'rtacha kun. */
  readonly actualLeadDays: string | null;
  /** `actual − promised`. Musbat — va'dadan kech. */
  readonly leadTimeBiasDays: string | null;
  readonly onTimePct: string | null;
  readonly receivedQty: string;
  readonly shortageQty: string;
  readonly shortagePct: string | null;
  /** Vozvratdan olingan brak miqdori (faqat bir manbali tovarlar). */
  readonly defectQty: string;
  readonly defectPct: string | null;
  /** Brak bog'lanishi MUMKIN bo'lgan tovarlar soni. */
  readonly soleSourceProductCount: number;
  readonly productCount: number;
  /** FAQAT `product.cost.view` bilan keladi. */
  readonly recentAvgPriceBase?: string | null;
  readonly previousAvgPriceBase?: string | null;
  readonly priceChangePct?: string | null;
  /** Birlashgan ball (0..100). Komponentlardan biri o'lchanmasa `null`. */
  readonly reliabilityScore: string | null;
  readonly reliabilityReason: InsufficientReason | null;
}

export interface SupplierScorecardReport {
  readonly meta: ReportMeta;
  readonly params: {
    readonly lookbackDays: number;
    readonly weights: {
      readonly onTime: string;
      readonly quality: string;
      readonly completeness: string;
    };
  };
  readonly formula: string;
  readonly summary: {
    readonly total: number;
    readonly scored: number;
    /** Ta'minotchiga BOG'LANMAGAN brak — ko'p manbali tovar. */
    readonly unattributedDefectQty: string;
  };
  readonly rows: readonly SupplierScoreRow[];
}

export const SUPPLIER_SCORECARD_DEFAULT_DAYS = 365;

export function fetchSupplierScorecard(
  lookbackDays: number,
  signal?: AbortSignal,
): Promise<SupplierScorecardReport> {
  return api.get<SupplierScorecardReport>('/reports/supplier-scorecard', {
    query: { lookbackDays },
    ...(signal ? { signal } : {}),
  });
}

// ---------------------------------------------------------------------------
// 17. Konteyner samaradorligi — GET /reports/container-efficiency  (landedcost.view)
// ---------------------------------------------------------------------------

export interface ContainerRow {
  readonly shipmentId: number;
  readonly docNo: string;
  readonly containerNo: string | null;
  readonly supplierId: number | null;
  readonly supplierName: string | null;
  readonly etd: string | null;
  readonly eta: string | null;
  readonly atd: string | null;
  readonly ata: string | null;
  readonly status: string;
  /**
   * `ata − eta`. `null` — O'LCHANMADI (sana yo'q), «o'z vaqtida» EMAS.
   *
   * `shipment-profitability` bu yerda `0` qaytaradi va shu sababli u ikki
   * boshqa hodisani bitta raqamga qo'shadi (server ramkasi).
   */
  readonly delayDays: number | null;
  readonly plannedTransitDays: number | null;
  readonly actualTransitDays: number | null;
  readonly skuCount: number;
  readonly receivedQty: string;
  readonly purchaseValueBase: string;
  readonly shipmentCostBase: string;
  /** Xarajat tovar narxining necha foizi. 100% dan oshishi MUMKIN. */
  readonly costSharePct: string | null;
}

export interface ContainerCostTypeRow {
  readonly costTypeCode: string;
  readonly costTypeName: string;
  readonly amountBase: string;
  readonly lineCount: number;
  readonly sharePct: string | null;
}

export interface ContainerEfficiencyReport {
  readonly meta: ReportMeta;
  readonly params: { readonly lookbackDays: number };
  readonly formula: string;
  readonly summary: {
    readonly total: number;
    readonly measured: number;
    readonly unmeasured: number;
    readonly avgDelayDays: string | null;
    readonly onTimePct: string | null;
    readonly message: string;
  };
  readonly byCostType: readonly ContainerCostTypeRow[];
  readonly rows: readonly ContainerRow[];
}

export const CONTAINER_DEFAULT_DAYS = 730;

export function fetchContainerEfficiency(
  lookbackDays: number,
  signal?: AbortSignal,
): Promise<ContainerEfficiencyReport> {
  return api.get<ContainerEfficiencyReport>('/reports/container-efficiency', {
    query: { lookbackDays },
    ...(signal ? { signal } : {}),
  });
}

// ---------------------------------------------------------------------------
// 18. Ombor yoshi — GET /reports/stock-age  (stock.view + stock.detail.view)
// ---------------------------------------------------------------------------

/** Guruh kodlari serverdagi `STOCK_AGE_BUCKETS` bilan AYNAN bir xil. */
export const STOCK_AGE_BUCKET_CODES = [
  'd0_30',
  'd31_60',
  'd61_90',
  'd91_180',
  'd180plus',
] as const;
export type StockAgeBucketCode = (typeof STOCK_AGE_BUCKET_CODES)[number];

export interface StockAgeRow {
  readonly productId: number;
  readonly sku: string;
  readonly name: string;
  readonly warehouseId: number;
  readonly warehouseCode: string;
  readonly onHand: string;
  /** Kirim yozuvi topilmagan miqdor — «eng eski» guruhga QO'SHILMAYDI. */
  readonly unknownAgeQty: string;
  readonly unknownAgeValue?: string;
  readonly buckets: Readonly<Record<string, string>>;
  /** FAQAT `stock.value.view` bilan keladi. */
  readonly value?: string;
  readonly coveredQty: string;
  /** Miqdor bo'yicha o'rtachalangan yosh. `null` — qoldiq nol. */
  readonly avgAgeDays: string | null;
  readonly avgAgeReason: InsufficientReason | null;
  readonly oldestAgeDays: number;
}

export interface StockAgeBucketTotal {
  readonly code: string;
  readonly fromDays: number;
  readonly toDays: number | null;
  readonly quantity: string;
}

export interface StockAgeReport {
  readonly meta: ReportMeta;
  readonly formula: string;
  readonly buckets: readonly StockAgeBucketTotal[];
  readonly summary: {
    readonly total: number;
    readonly unknownAgePositions: number;
    readonly unknownAgeQty: string;
  };
  readonly rows: readonly StockAgeRow[];
}

export function fetchStockAge(
  warehouseId: number | undefined,
  signal?: AbortSignal,
): Promise<StockAgeReport> {
  return api.get<StockAgeReport>('/reports/stock-age', {
    query: { warehouseId },
    ...(signal ? { signal } : {}),
  });
}

// ---------------------------------------------------------------------------
// 19. Mijoz foydaliligi — GET /reports/customer-profitability  (so.margin.view)
// ---------------------------------------------------------------------------

export interface CustomerProfitRow {
  readonly customerId: number;
  readonly customerCode: string;
  readonly customerName: string;
  readonly orderCount: number;
  readonly quantity: string;
  /** RO'YXAT narxidagi tushum (bonus satrlarsiz — server izohi). */
  readonly listRevenue: string;
  /** Chegirmadan keyingi haqiqiy tushum. */
  readonly netRevenue: string;
  readonly discount: string;
  readonly discountPct: string | null;
  readonly cogs: string;
  /** Bonus tovarning TANNARXI — chegirma emas, xarajat (§12A.3). */
  readonly bonusCost: string;
  readonly returnsBase: string;
  readonly netProfit: string;
  readonly netMarginPct: string | null;
  readonly marginReason: InsufficientReason | null;
}

export interface CustomerProfitReport {
  readonly meta: ReportMeta;
  readonly formula: string;
  /**
   * Hisobga OLINMAGAN xarajatlar — serverdan keladi.
   *
   * Ekran o'zi yozib qo'ymaydi: chegara serverda belgilangan va yetkazish
   * xarajati o'lchanadigan bo'lganda ekran emas, javob o'zgaradi.
   */
  readonly excludes: readonly string[];
  readonly summary: {
    readonly total: number;
    readonly lossMaking: number;
    readonly message: string;
  };
  readonly rows: readonly CustomerProfitRow[];
}

export function fetchCustomerProfitability(
  period: Period,
  signal?: AbortSignal,
): Promise<CustomerProfitReport> {
  return api.get<CustomerProfitReport>('/reports/customer-profitability', {
    query: { from: period.from, to: period.to },
    ...(signal ? { signal } : {}),
  });
}

// ---------------------------------------------------------------------------
// Query kalitlari
// ---------------------------------------------------------------------------

/**
 * Kalit tarkibiga FILTR ham kiradi.
 *
 * Aks holda davr o'zgarganda react-query keshdan eski davr natijasini
 * qaytaradi va ekranda «avgust» yozuvi ostida iyul raqamlari turadi —
 * bu jimgina yuz beradigan va eng qimmat xatolardan biri.
 */
export const reportKeys = {
  all: ['reports'] as const,
  stock: (filters: StockFilters) => ['reports', 'stock', filters] as const,
  /*
   * Chegara kalit tarkibida: `deadStockDays` o'zgarsa `deadStockCount` ham
   * o'zgaradi, kesh esa eski chegaradagi sonni ushlab qolardi.
   */
  stockSummary: (deadStockDays?: number) =>
    ['reports', 'stock-summary', deadStockDays ?? 'default'] as const,
  stockValuation: (filters: StockFilters) => ['reports', 'stock-valuation', filters] as const,
  sales: (period: Period, groupBy: SalesGroupBy) => ['reports', 'sales', period, groupBy] as const,
  grossProfit: (period: Period, by: ProfitBy) => ['reports', 'gross-profit', period, by] as const,
  arAging: (asOf: string | undefined) => ['reports', 'ar-aging', asOf ?? 'today'] as const,
  /* Kalitga MIJOZ ham kiradi: aks holda ikkinchi diler birinchisining keshini oladi. */
  customerDiscipline: (customerId: number | undefined, from: string, to: string, bucket: string) =>
    ['reports', 'customer-discipline', customerId ?? 0, from, to, bucket] as const,
  disciplineOverview: (month: string) => ['reports', 'discipline-overview', month] as const,
  disciplineNormDefaults: ['reports', 'discipline-norm'] as const,
  // Parametrsiz — endpoint ham parametr olmaydi (`fetchApAging` izohi).
  apAging: ['reports', 'ap-aging'] as const,
  cashFlow: (period: Period) => ['reports', 'cash-flow', period] as const,
  deadStock: (days: number) => ['reports', 'dead-stock', days] as const,
  landedCost: (shipmentId: number) => ['reports', 'landed-cost', shipmentId] as const,
  shipmentProfit: (shipmentId: number | undefined) =>
    ['reports', 'shipment-profitability', shipmentId ?? 'all'] as const,
  reorder: (params: ReorderParams) => ['reports', 'reorder', params] as const,

  /*
   * Tahlil hisobotlari. Kalit tarkibiga CHEGARA ham kiradi (davr bilan bir
   * xil sabab): 80/15 va 70/20 chegaralarida bir tovar boshqa guruhga
   * tushadi, kesh esa eski guruhni ushlab qolardi va ekranda «A» yozuvi
   * ostida boshqa chegaraning natijasi turardi.
   */
  abc: (period: Period, params: AbcParams) => ['reports', 'abc', period, params] as const,
  xyz: (params: XyzParams) => ['reports', 'xyz', params] as const,
  rfm: (period: Period) => ['reports', 'rfm', period] as const,
  ltv: (minLifespanDays: number) => ['reports', 'ltv', minLifespanDays] as const,
  supplierScorecard: (lookbackDays: number) =>
    ['reports', 'supplier-scorecard', lookbackDays] as const,
  containerEfficiency: (lookbackDays: number) =>
    ['reports', 'container-efficiency', lookbackDays] as const,
  stockAge: (warehouseId: number | undefined) =>
    ['reports', 'stock-age', warehouseId ?? 'all'] as const,
  customerProfitability: (period: Period) =>
    ['reports', 'customer-profitability', period] as const,
};
